import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "../lib/errors";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

// Global axios response interceptor — logs the server detail to the browser
// console so demo hiccups + customer issues are diagnosable in real time,
// and surfaces 401-revoked sessions to the auth flow. Installed once at
// module load to avoid duplicate registration.
let interceptorInstalled = false;
function installAxiosInterceptor(onAuthFailure) {
  if (interceptorInstalled) return;
  interceptorInstalled = true;
  axios.interceptors.response.use(
    (resp) => resp,
    (err) => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const url = err?.config?.url || "";
      if (detail) {
        const formatted = formatApiErrorDetail(detail);
        // eslint-disable-next-line no-console
        console.warn(`[API ${status}] ${url} → ${formatted}`);
      } else if (status >= 500) {
        // eslint-disable-next-line no-console
        console.error(`[API ${status}] ${url}`, err?.response?.data || err.message);
      }
      // If our hardened auth middleware revoked the session, drop the user
      // back to /login automatically. Also catches 403s from admin routes
      // where the JWT's role doesn't match the user's current DB role
      // (i.e., role was changed after the token was issued).
      const isAdminScope = typeof url === "string" && (
        url.includes("/api/admin/") ||
        url.includes("/api/tenants") ||
        url.includes("/api/tenant-admin/")
      );
      const detailLooksLikeRevocation = typeof detail === "string" && (
        detail.includes("revoked") ||
        detail.includes("context changed") ||
        detail.includes("credential change") ||
        detail.includes("Invalid or expired") ||
        detail.includes("Invalid token")
      );
      if (
        (status === 401 && detailLooksLikeRevocation) ||
        (status === 403 && isAdminScope)
      ) {
        if (onAuthFailure) onAuthFailure();
      }
      return Promise.reject(err);
    },
  );
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("access_token"));
  const [loading, setLoading] = useState(true);

  // Install the global axios interceptor exactly once. The auth-failure
  // callback wipes local credentials and bounces the user to /login.
  useEffect(() => {
    installAxiosInterceptor(() => {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("impersonating_tenant_id");
      localStorage.removeItem("impersonating_tenant_name");
      // Avoid recursive reloads if we're already on /login
      if (!window.location.pathname.startsWith("/login")) {
        window.location.assign("/login");
      }
    });
  }, []);

  // Configure axios defaults when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      localStorage.setItem("access_token", token);
    } else {
      delete axios.defaults.headers.common["Authorization"];
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    }
  }, [token]);

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    axios
      .get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setUser(res.data);
      })
      .catch(() => {
        // Token invalid, try refresh
        const refreshToken = localStorage.getItem("refresh_token");
        if (refreshToken) {
          axios
            .post(`${API_URL}/api/auth/refresh`, { refresh_token: refreshToken })
            .then((res) => {
              setToken(res.data.access_token);
              localStorage.setItem("refresh_token", res.data.refresh_token);
              return axios.get(`${API_URL}/api/auth/me`, {
                headers: { Authorization: `Bearer ${res.data.access_token}` },
              });
            })
            .then((res) => setUser(res.data))
            .catch(() => {
              setToken(null);
              setUser(null);
            });
        } else {
          setToken(null);
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    // MFA step-up: no tokens issued yet — the caller shows the code prompt
    if (res.data.mfa_required) return res.data;
    setToken(res.data.access_token);
    localStorage.setItem("refresh_token", res.data.refresh_token);
    // Login response doesn't include subscription/features — fetch the full
    // /me payload so the sidebar can render the user's allowed nav items.
    try {
      const me = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${res.data.access_token}` },
      });
      setUser(me.data);
    } catch {
      // Fallback to the lean login payload — at least the user is authenticated
      setUser(res.data.user);
    }
    return res.data;
  }, []);

  const completeMfa = useCallback(async (mfaToken, code) => {
    const res = await axios.post(`${API_URL}/api/auth/mfa/verify`, {
      mfa_token: mfaToken,
      code,
    });
    setToken(res.data.access_token);
    localStorage.setItem("refresh_token", res.data.refresh_token);
    try {
      const me = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${res.data.access_token}` },
      });
      setUser(me.data);
    } catch {
      setUser(res.data.user);
    }
    return res.data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const res = await axios.post(`${API_URL}/api/auth/register`, {
      email,
      password,
      name,
      role: "analyst",
    });
    setToken(res.data.access_token);
    localStorage.setItem("refresh_token", res.data.refresh_token);
    try {
      const me = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${res.data.access_token}` },
      });
      setUser(me.data);
    } catch {
      setUser(res.data.user);
    }
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    // Best-effort revocation on the server — still wipe local state even if the
    // network call fails (offline / token already expired).
    try {
      const localToken = localStorage.getItem("access_token");
      if (localToken) {
        await axios.post(`${API_URL}/api/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${localToken}` },
          timeout: 5000,
        });
      }
    } catch {
      // ignore — we're logging out regardless
    }
    setToken(null);
    setUser(null);
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    delete axios.defaults.headers.common["Authorization"];
  }, []);

  // Axios interceptor for 401 responses
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && token) {
          const refreshToken = localStorage.getItem("refresh_token");
          if (refreshToken && !error.config._retry) {
            error.config._retry = true;
            try {
              const res = await axios.post(`${API_URL}/api/auth/refresh`, {
                refresh_token: refreshToken,
              });
              setToken(res.data.access_token);
              localStorage.setItem("refresh_token", res.data.refresh_token);
              error.config.headers["Authorization"] = `Bearer ${res.data.access_token}`;
              return axios(error.config);
            } catch {
              logout();
            }
          } else {
            logout();
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token, logout]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, completeMfa, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
